import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import {
  Menu,
  User,
  Settings,
  Shield,
  LayoutDashboard,
  Receipt,
  Tag,
  BarChart3,
  Target,
  Gem,
  Wallet,
  Bell,
  MessageCircle,
  HelpCircle,
  ChevronLeft,
  CreditCard,
  Lock
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import DraggableChatButton from './DraggableChatButton';
import AIChat from './AIChat';
import AppTour from './AppTour';
import NotificationBell from './NotificationBell';
import OnboardingFlow from './OnboardingFlow';
import TrialBanner from './TrialBanner';
import { APP_VERSION } from '@/config/version';
import { useIsMobile } from '@/hooks/useIsMobile';
import MobileLayout from './MobileLayout';

const Layout = ({ children }) => {
  // eslint-disable-next-line no-unused-vars
  const { user, logout, token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    return localStorage.getItem('desktop_sidebar_collapsed') === 'true';
  });

  const toggleDesktopSidebar = () => {
    setDesktopCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('desktop_sidebar_collapsed', String(next));
      return next;
    });
  };
  const [chatOpen, setChatOpen] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(
    localStorage.getItem('pgastos_onboarding_done') === 'true'
  );
  const [runTour, setRunTour] = useState(false);
  const [tourDisabled, setTourDisabled] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navRef = useRef(null);


  // ─── Smart Header (Ocultar al bajar, mostrar al subir) ───
  useEffect(() => {
    if (isMobile) return; // Skip logic on mobile if needed, but hook must be called

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setShowHeader(true);
        setLastScrollY(currentScrollY);
        return;
      }

      if (currentScrollY > lastScrollY + 2) {
        setShowHeader(false);
      } else if (currentScrollY < lastScrollY - 2) {
        setShowHeader(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, isMobile]);

  // ─── Bloquear scroll del body cuando sidebar está abierto ───
  useEffect(() => {
    if (isMobile) return;

    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen, isMobile]);

  // ─── Verificar si el tour está desactivado permanentemente ───
  useEffect(() => {
    if (isMobile) return;

    const disabled = localStorage.getItem('tourDisabledPermanently');
    setTourDisabled(disabled === 'true');

    // Auto-lanzar tour para nuevos usuarios (solo si ya terminó el onboarding)
    if (!disabled && onboardingDone) {
      const hasSeenTour = localStorage.getItem('hasSeenTour');
      if (!hasSeenTour && user && location.pathname === '/dashboard') {
        // Esperar un momento extra para que el Dashboard cargue
        setTimeout(() => {
          setRunTour(true);
          localStorage.setItem('hasSeenTour', 'true');
        }, 1500);
      }
    }
  }, [user, location.pathname, onboardingDone, isMobile]);

  // Si es móvil, usamos el nuevo layout premium
  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  const handleDisableTourPermanently = () => {
    localStorage.setItem('tourDisabledPermanently', 'true');
    setTourDisabled(true);
    setRunTour(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleEnableTour = () => {
    localStorage.removeItem('tourDisabledPermanently');
    localStorage.removeItem('hasSeenTour');
    setTourDisabled(false);
    showToast('Tour reactivado. Ahora puedes verlo desde el sidebar', 'success');
  };



  const handleProfileClick = (e, tab = 'general') => {
    e.preventDefault();
    setSidebarOpen(false);
    const targetPath = tab === 'general' ? '/profile' : `/profile?tab=${tab}`;
    
    if (location.pathname === '/profile') {
      navigate(targetPath, { replace: true });
      if (tab === 'general') {
        window.dispatchEvent(new CustomEvent('reset-profile-tabs'));
      }
    } else {
      navigate(targetPath);
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/transactions', label: 'Transacciones', icon: Receipt },
    { path: '/debts', label: 'Control de Deudas', icon: CreditCard },
    { path: '/accounts', label: 'Cuentas', icon: Wallet },
    { path: '/payment-reminders', label: 'Recordatorios', icon: Bell },
    { path: '/categories', label: 'Categorías', icon: Tag },
    { path: '/statistics', label: 'Estadísticas', icon: BarChart3 },
    { path: '/goals', label: 'Objetivos', icon: Target, premium: true },
    { path: '/advanced-analytics', label: 'Análisis Avanzados', icon: Gem, pro: true }
  ];

  if (isMobile) {
    return <MobileLayout>{children}</MobileLayout>;
  }

  return (
    <div className="h-screen w-full flex bg-slate-50 overflow-hidden">
      {/* Onboarding Flow - Solo aparece en dispositivos donde nunca se ha completado */}
      {!onboardingDone && user && (
        <OnboardingFlow 
          onComplete={() => {
            setOnboardingDone(true);
            localStorage.setItem('pgastos_onboarding_done', 'true');
          }} 
          token={token} 
        />
      )}

      {/* Overlay para cerrar sidebar en móvil y PWA */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          style={{ touchAction: 'none' }}
          onClick={() => setSidebarOpen(false)}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}

      {/* Sidebar - Fijo e inamovible en escritorio */}
      <aside className={`
        fixed lg:static top-0 left-0 z-50
        ${desktopCollapsed ? 'lg:w-20' : 'lg:w-64'} w-64 h-full bg-white border-r border-slate-200
        transform transition-all duration-300 ease-in-out
        flex-shrink-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Botón flotante para colapsar/expandir en la línea divisoria (Solo Desktop) */}
        <button
          onClick={toggleDesktopSidebar}
          className="hidden lg:flex absolute -right-3.5 top-6 z-50 w-7 h-7 rounded-full bg-white border border-slate-200 shadow-md items-center justify-center text-slate-600 hover:text-blue-600 hover:scale-110 active:scale-95 transition-all cursor-pointer"
          title={desktopCollapsed ? "Expandir menú" : "Colapsar menú"}
        >
          <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${desktopCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div className="flex flex-col h-full overflow-hidden relative" style={{ touchAction: 'pan-y' }}>
          {/* Logo & Version */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <Link to="/dashboard" className={`flex items-center gap-3 ${desktopCollapsed ? 'justify-center' : ''}`}>
              <img
                src="/icon-512x512.png"
                alt="Portal de Gastos"
                className="w-9 h-9 flex-shrink-0"
              />
              {!desktopCollapsed && (
                <div className="min-w-0 animate-fade-in">
                  <h1 className="font-bold text-lg text-slate-900 truncate">PGastos</h1>
                  <p className="text-xs text-slate-400">v{APP_VERSION}</p>
                </div>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav ref={navRef} className="flex-1 overflow-y-scroll p-4 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
            <ul className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const requiresPremium = item.premium && user?.subscription_tier === 'free';
                const requiresPro = item.pro && (user?.subscription_tier === 'free' || user?.subscription_tier === 'premium');

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      title={desktopCollapsed ? item.label : undefined}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                        ${isActive
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                        }
                        ${(requiresPremium || requiresPro) ? 'opacity-60' : ''}
                        ${desktopCollapsed ? 'lg:justify-center lg:px-2' : ''}
                      `}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!desktopCollapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {requiresPremium && (
                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Lock className="w-2.5 h-2.5 shrink-0" />
                              Premium
                            </span>
                          )}
                          {requiresPro && (
                            <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Lock className="w-2.5 h-2.5 shrink-0" />
                              Pro
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-slate-200">
            <button 
              onClick={(e) => handleProfileClick(e)}
              className={`w-full text-left block mb-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:shadow-md transition-shadow ${
                desktopCollapsed ? 'p-2' : 'p-3'
              }`}
              title={desktopCollapsed ? (user?.nickname || user?.username) : undefined}
            >
              <div className={`flex items-center gap-3 ${desktopCollapsed ? 'justify-center' : ''}`}>
                {/* Foto de perfil */}
                <div className="relative flex-shrink-0">
                  {user?.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt={user?.nickname || user?.username}
                      className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center border-2 border-white shadow-sm">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                {!desktopCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user?.nickname || user?.username}</p>
                    <p className="text-xs text-slate-600 truncate">{user?.email}</p>
                  </div>
                )}
              </div>

              {!desktopCollapsed && (
                <div 
                  className="flex items-center gap-1 mt-2 cursor-pointer hover:scale-105 transition-transform"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProfileClick(e, 'subscription');
                  }}
                >
                  <div className={`w-2 h-2 rounded-full ${user?.subscription_tier === 'pro' ? 'bg-purple-600' :
                      user?.subscription_tier === 'premium' ? 'bg-blue-600' : 'bg-slate-400'
                    }`}></div>
                  <span className={`text-xs font-medium ${user?.subscription_tier === 'pro' ? 'text-purple-700' :
                      user?.subscription_tier === 'premium' ? 'text-blue-700' : 'text-slate-600'
                    }`}>
                    {user?.subscription_tier === 'pro' ? 'Plan Pro' :
                      user?.subscription_tier === 'premium' ? 'Plan Premium' : 'Usuario Free'}
                  </span>
                </div>
              )}
            </button>

            <ul className="space-y-1">
              {/* Tour Guiado */}
              {!tourDisabled && (
                <li>
                  <button
                    onClick={() => setRunTour(true)}
                    title={desktopCollapsed ? "Tour Guiado" : undefined}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ${
                      desktopCollapsed ? 'justify-center px-2' : ''
                    }`}
                  >
                    <HelpCircle className="w-5 h-5 flex-shrink-0" />
                    {!desktopCollapsed && <span>Tour Guiado</span>}
                  </button>
                </li>
              )}
              {user?.role === 'admin' && (
                <li>
                  <Link
                    to="/admin"
                    onClick={() => setSidebarOpen(false)}
                    title={desktopCollapsed ? "Admin Panel" : undefined}
                    className={`flex items-center gap-3 px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors ${
                      desktopCollapsed ? 'justify-center px-2' : ''
                    }`}
                  >
                    <Shield className="w-5 h-5 flex-shrink-0" />
                    {!desktopCollapsed && <span>Admin Panel</span>}
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen w-full min-w-0 overflow-y-auto overflow-x-hidden">
        {/* Mobile Header - Pro Hybrid Header */}
        <header className={`
          lg:hidden fixed top-0 left-0 right-0 z-40
          bg-white/90 backdrop-blur-lg border-b border-slate-200/50 p-4
          transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
          ${showHeader ? 'translate-y-0 opacity-100 shadow-sm' : '-translate-y-full opacity-0 shadow-none'}
        `}>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-slate-100 active:bg-slate-200 rounded-full transition-all"
            >
              <Menu className="w-6 h-6 text-slate-700" />
            </button>
            <div className="flex items-center gap-2">
              <img
                src="/icon-512x512.png"
                alt="Portal de Gastos"
                className="w-7 h-7"
              />
              <span className="font-extrabold text-slate-900 tracking-tighter text-lg">PGastos</span>
            </div>
            <NotificationBell />
          </div>
        </header>

        {/* Espaciador para el header fijo en móvil */}
        <div className="h-[72px] lg:hidden" />

        {/* Trial Banner */}
        <TrialBanner />

        {/* Page Content */}
        <main className="flex-1 w-full min-w-0 overflow-x-hidden">
          {children}
        </main>
      </div>

      {/* App Tour */}
      {!tourDisabled && (
        <AppTour
          run={runTour}
          onFinish={() => setRunTour(false)}
          onDisable={handleDisableTourPermanently}
          userTier={user?.subscription_tier || 'free'}
          tourType="full"
        />
      )}

      {/* Chat IA Floating Button - Solo para usuarios Premium/Pro */}
      {user && (user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && (
        <DraggableChatButton />
      )}

      {/* Notification Bell Floating - Solo para Desktop */}
      <div className="hidden lg:block">
        <NotificationBell />
      </div>
    </div>
  );
};

export default Layout;
